import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ip_back } from "../../config";
import { toAbsoluteMediaUrl } from "../../utils/kotPlayerMatches";
import { ZONE_CHART_COLORS, shortZoneLabel } from "../../features/trainingSummary/zoneUtils";
import HRChart from "../ExerciseHRChart";
import "./AutoevalGuideCard.css";

const PERF_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"];
const EXERTION_COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];
const PERF_ICONS = ["😞", "😕", "😐", "🙂", "🤩"];
const EXERTION_ICONS = ["💤", "🚶", "🏃", "🔥", "💥"];

function RatingRow({ value, onChange, colors, icons, disabled, name }) {
  return (
    <div className="dp-ae__rating-row" role="group" aria-label={name}>
      {[1, 2, 3, 4, 5].map((level) => {
        const active = value === level;
        const color = colors[level - 1];
        return (
          <button
            key={level}
            type="button"
            disabled={disabled}
            className={`dp-ae__rate${active ? " dp-ae__rate--active" : ""}`}
            style={
              active
                ? { backgroundColor: color, borderColor: color, color: "#fff" }
                : { borderColor: `${color}66` }
            }
            onClick={() => onChange(level)}
            aria-pressed={active}
          >
            <span aria-hidden="true">{icons[level - 1]}</span>
            <span>{level}</span>
          </button>
        );
      })}
    </div>
  );
}

function ChatEvalVideo({ videos, t }) {
  const [index, setIndex] = useState(0);
  const clips = Array.isArray(videos) ? videos.filter((v) => v?.url) : [];
  if (!clips.length) return null;
  const safeIndex = Math.min(index, clips.length - 1);
  const clip = clips[safeIndex];
  const src = toAbsoluteMediaUrl(clip.url, ip_back);
  if (!src) return null;

  return (
    <div className="dp-ae__video">
      <video
        key={clip.message_id || src}
        className="dp-ae__video-el"
        src={src}
        controls
        playsInline
        preload="metadata"
      >
        {t("ae_chat_video_unsupported", "Your browser cannot play this evaluation video.")}
      </video>
      {clips.length > 1 ? (
        <div className="dp-ae__video-dots" role="tablist" aria-label={t("evaluationVideo", "Evaluation video")}>
          {clips.map((c, i) => (
            <button
              key={c.message_id || i}
              type="button"
              className={`dp-ae__dot${i === safeIndex ? " dp-ae__dot--on" : ""}`}
              aria-label={`${t("evaluationVideo", "Evaluation video")} ${i + 1}`}
              aria-selected={i === safeIndex}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AutoevalGuideCard({
  guide,
  disabled = false,
  submitted = false,
  onSubmit,
}) {
  const { t } = useTranslation();
  const [perf, setPerf] = useState(null);
  const [exertion, setExertion] = useState(null);
  const [localDone, setLocalDone] = useState(false);

  const done = submitted || localDone;
  const hr = guide?.hr;
  const comments = Array.isArray(guide?.comments) ? guide.comments : [];
  const playerNotes = Array.isArray(guide?.playerNotes) ? guide.playerNotes : [];
  const remaining = Number(guide?.remaining_count) || 0;

  const hrInfo = useMemo(() => {
    if (!hr?.available) return null;
    return {
      averageHR: hr.averageHR,
      minHR: hr.minHR,
      maxHR: hr.maxHR,
      readings: hr.readings,
      hrGraph: hr.hrGraph,
      summary_duration_sec: hr.summary_duration_sec,
    };
  }, [hr]);

  const canSubmit = Boolean(perf && exertion && !disabled && !done && guide?.te_id);

  const handleSubmit = () => {
    if (!canSubmit) return;
    setLocalDone(true);
    const label = t("ae_chat_rated_as", {
      defaultValue: "Performance {{perf}} · Effort {{effort}}",
      perf,
      effort: exertion,
    });
    onSubmit?.({
      te_id: guide.te_id,
      exercise_perf: perf,
      perceived_exertion: exertion,
      label,
    });
  };

  if (!guide?.te_id) return null;

  return (
    <section className="dp-ae" aria-label={t("ae_title", "Self-evaluation")}>
      <header className="dp-ae__head">
        <div>
          <p className="dp-ae__kicker">
            {remaining <= 1
              ? t("ae_chat_last", "Last exercise")
              : t("ae_chat_remaining", {
                  defaultValue: "{{count}} exercises left",
                  count: remaining,
                })}
          </p>
          <h3 className="dp-ae__title">{guide.ex_name || t("exercise", "Exercise")}</h3>
          {guide.schema_name ? (
            <p className="dp-ae__schema">
              {guide.schema_name}
              {guide.schema_niveaux ? ` · ${guide.schema_niveaux}` : ""}
            </p>
          ) : null}
        </div>
        <div className="dp-ae__meta">
          {guide.te_duration ? (
            <span className="dp-ae__pill">{guide.te_duration} {t("min", "min")}</span>
          ) : null}
          {guide.smart_required ? (
            <span className="dp-ae__pill dp-ae__pill--smart">
              {guide.smart_goal_label || "SMART"}
            </span>
          ) : null}
        </div>
      </header>

      {guide.objective ? <p className="dp-ae__objective">{guide.objective}</p> : null}

      <ChatEvalVideo videos={guide.videos} t={t} />
      {!guide.videos?.length ? (
        <p className="dp-ae__empty">{t("ae_chat_no_video", "No evaluation video for this drill.")}</p>
      ) : null}

      {hrInfo ? (
        <div className="dp-ae__hr">
          <HRChart
            info={hrInfo}
            mhr={hr.mhr || 180}
            compact
            forceDark
            accent="#2A9BB5"
            labels={{
              heading: t("heartRate", "Heart rate"),
              duration: t("duration", "Duration"),
              hrAvg: t("hrAvg", "Avg HR"),
              hrMin: t("hrMin", "Min HR"),
              hrMax: t("hrMax", "Max HR"),
            }}
          />
          {Array.isArray(hr.zones) && hr.zones.length ? (
            <div className="dp-ae__zones">
              {hr.zones.map((z) => (
                <span
                  key={z.zone}
                  className="dp-ae__zone"
                  style={{ "--z": ZONE_CHART_COLORS[z.zone] || "#94a3b8" }}
                >
                  {shortZoneLabel(z.zone)} · {z.pct}%
                </span>
              ))}
            </div>
          ) : null}
          {hr.explanation ? <p className="dp-ae__explain">{hr.explanation}</p> : null}
        </div>
      ) : (
        <p className="dp-ae__explain dp-ae__explain--muted">
          {hr?.explanation || t("ae_chat_no_hr", "No heart-rate recording for this drill.")}
        </p>
      )}

      {comments.length ? (
        <div className="dp-ae__notes">
          {comments.map((c, i) => (
            <blockquote key={`${c.source}-${i}`} className={`dp-ae__note dp-ae__note--${c.source}`}>
              <span className="dp-ae__note-who">
                {c.source === "sparring"
                  ? t("sparring", "Sparring")
                  : t("coach", "Coach")}
                {c.sender_name ? ` · ${c.sender_name}` : ""}
              </span>
              <p>{c.text}</p>
            </blockquote>
          ))}
        </div>
      ) : null}

      {playerNotes.length ? (
        <div className="dp-ae__notes">
          {playerNotes.map((text, i) => (
            <blockquote key={`me-${i}`} className="dp-ae__note dp-ae__note--self">
              <span className="dp-ae__note-who">{t("ae_chat_your_note", "Your note")}</span>
              <p>{text}</p>
            </blockquote>
          ))}
        </div>
      ) : null}

      {done ? (
        <p className="dp-ae__saved">{t("ae_success", "Evaluations saved successfully!")}</p>
      ) : (
        <div className="dp-ae__form">
          <label className="dp-ae__q">
            🎯{" "}
            {guide.schema_name
              ? t("ae_schema_perf", "Did this schéma hold?")
              : t("ae_performance_rate", "Rate your Performance:")}
          </label>
          <RatingRow
            name={t("ae_performance", "Performance")}
            value={perf}
            onChange={setPerf}
            colors={PERF_COLORS}
            icons={PERF_ICONS}
            disabled={disabled}
          />
          <label className="dp-ae__q">
            💪 {t("ae_exertion_rate", "Rate your Effort:")}
          </label>
          <RatingRow
            name={t("ae_exertion", "Perceived Exertion")}
            value={exertion}
            onChange={setExertion}
            colors={EXERTION_COLORS}
            icons={EXERTION_ICONS}
            disabled={disabled}
          />
          <button
            type="button"
            className="dp-ae__submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {t("submitMyEvaluation", "Submit my evaluation")}
          </button>
        </div>
      )}
    </section>
  );
}
